
import React, { useState, createContext, useContext, useCallback } from "react";
import { StrategyWithDetails, StrategyProvider } from "@/types/strategy-types";
import { SortField, SortOrder } from "./tableUtils";

interface StrategiesTableContextProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  statusFilter: string;
  setStatusFilter: (filter: string) => void;
  approvalFilter: string;
  setApprovalFilter: (filter: string) => void;
  providerFilter: string;
  setProviderFilter: (filter: string) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  isCreateDialogOpen: boolean;
  setIsCreateDialogOpen: (isOpen: boolean) => void;
  editingStrategy: StrategyWithDetails | null;
  setEditingStrategy: (strategy: StrategyWithDetails | null) => void;
  deletingStrategyId: string | null;
  setDeletingStrategyId: (id: string | null) => void;
  previewStrategy: StrategyWithDetails | null;
  setPreviewStrategy: (strategy: StrategyWithDetails | null) => void;
  sortField: SortField;
  setSortField: (field: SortField) => void;
  sortOrder: SortOrder;
  setSortOrder: (order: SortOrder) => void;
  columnFilters: {
    name: string;
    exchange: string;
    minInvestment: string;
    riskLevel: string;
  };
  setColumnFilters: React.Dispatch<
    React.SetStateAction<{
      name: string;
      exchange: string;
      minInvestment: string;
      riskLevel: string;
    }>
  >;
  handleColumnFilterChange: (field: keyof ColumnFiltersType, value: string) => void;
  handleSort: (field: SortField) => void;
}

// Define a type for column filters to use throughout the component
type ColumnFiltersType = {
  name: string;
  exchange: string;
  minInvestment: string;
  riskLevel: string;
};

const StrategiesTableContext = createContext<StrategiesTableContextProps | undefined>(undefined);

export const useStrategiesTable = () => {
  const context = useContext(StrategiesTableContext);
  if (!context) {
    throw new Error("useStrategiesTable must be used within a StrategiesTableProvider");
  }
  return context;
};

interface StrategiesTableProviderProps {
  children: React.ReactNode;
}

export const StrategiesTableProvider = ({ children }: StrategiesTableProviderProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [approvalFilter, setApprovalFilter] = useState<string>("all");
  const [providerFilter, setProviderFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingStrategy, setEditingStrategy] = useState<StrategyWithDetails | null>(null);
  const [deletingStrategyId, setDeletingStrategyId] = useState<string | null>(null);
  const [previewStrategy, setPreviewStrategy] = useState<StrategyWithDetails | null>(null);
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersType>({
    name: "",
    exchange: "",
    minInvestment: "",
    riskLevel: "",
  });

  const handleColumnFilterChange = useCallback((field: keyof ColumnFiltersType, value: string) => {
    setColumnFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  }, [sortField, sortOrder]);

  return (
    <StrategiesTableContext.Provider
      value={{
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        approvalFilter,
        setApprovalFilter,
        providerFilter,
        setProviderFilter,
        currentPage,
        setCurrentPage,
        isCreateDialogOpen,
        setIsCreateDialogOpen,
        editingStrategy,
        setEditingStrategy,
        deletingStrategyId,
        setDeletingStrategyId,
        previewStrategy,
        setPreviewStrategy,
        sortField,
        setSortField,
        sortOrder,
        setSortOrder,
        columnFilters,
        setColumnFilters,
        handleColumnFilterChange,
        handleSort
      }}
    >
      {children}
    </StrategiesTableContext.Provider>
  );
};
